/**
 * ConversationsPage.tsx — the Conversations list cockpit (story E0-BO-CONV7).
 *
 * The conversations sibling of `LeadsPage`. Consumes `GET /api/conversations`
 * through the injected API client and renders the MOST-RECENTLY-ACTIVE list with
 * filters (funnel stage, has-error, paused) and KEYSET pagination via the
 * envelope's opaque `next_cursor` ("load more"; the cursor ADVANCES — null =
 * end-of-list, NO offset/page-number assumptions).
 *
 * Each row deep-links to the CONV8 detail route (`#conversations/<thread>`) — the
 * navigation SEAM this story leaves; CONV8 builds the detail page. Where a thread
 * maps to a lead (`lead_id`), the row also deep-links into the Leads panel
 * (`#leads/<lead_id>`, the SPA's hash route for that lead).
 *
 * First-class states (spec §Error Handling): loading, empty, a clean error for
 * the BFF's 502, and a 401/403 → the shared UnauthorizedScreen — never a blank
 * page. When the envelope reports `detail_completeness !== 'single_replica'` the
 * page renders an honesty note so the operator knows the data is degraded.
 *
 * NO number-search in v1 (deferred per spec).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ApiClient, ConversationsQuery } from '../../api/client';
import type { ConversationListItem } from '../../api/types';
import { UnauthorizedScreen } from '../../auth/UnauthorizedScreen';
import { loadPersistedFilters, savePersistedFilters } from '../../lib/persistFilters';
import { formatDateTime } from '../leads/formatDateTime';
import { ConsentBadge, DeliveryBadge, FunnelStageBadge, PausedBadge } from './ConversationBadges';
import { ConversationsFilters } from './ConversationsFilters';
import { useConversationsQuery } from './useConversationsQuery';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { Notice } from '../../components/Notice';
import { Skeleton } from '../../components/Skeleton';
import floating from '../../lib/floating';

export interface ConversationsPageProps {
  apiClient: ApiClient;
  /** Thread open in the workbench's right pane; marks its row `aria-current`. */
  selectedThreadId?: string | null;
}

const DEFAULT_QUERY: ConversationsQuery = { limit: 50 };
/** sessionStorage key for the last-applied Conversations filter set. */
const CONVERSATIONS_FILTERS_KEY = 'tcn.backoffice.conversations.filters.v1';

const persistableQuery = (query: ConversationsQuery): ConversationsQuery => {
  const rest = { ...query };
  delete rest.cursor;
  return rest;
};

const initialQuery = (): ConversationsQuery =>
  loadPersistedFilters<ConversationsQuery>(CONVERSATIONS_FILTERS_KEY) ?? DEFAULT_QUERY;
/** The ONE status mark per row: paused > delivery problem > funnel stage. */
const deliveryIsMark = (conv: ConversationListItem): boolean =>
  !conv.is_paused && (conv.delivery_status === 'error' || conv.delivery_status === 'stuck');

const StatusMark = ({ conv }: { conv: ConversationListItem }) => {
  if (conv.is_paused) return <PausedBadge paused />;
  if (deliveryIsMark(conv)) return <DeliveryBadge status={conv.delivery_status} />;
  return conv.funnel_stage ? (
    <FunnelStageBadge stage={conv.funnel_stage} />
  ) : (
    <span className="text-ink-3">—</span>
  );
};

const linkClass =
  'rounded underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-outline-focus';

const ConversationsList = ({
  items,
  locale,
  selectedThreadId,
}: {
  items: ConversationListItem[];
  locale: string;
  selectedThreadId: string | null;
}) => {
  const { t } = useTranslation();
  return (
    <ul
      aria-label={t('conversations.tableLabel')}
      className="m-0 list-none p-0"
      data-testid="conversations-table"
    >
      {items.map((conv) => {
        const selected = conv.thread_id === selectedThreadId;
        const stageIsMark = !conv.is_paused && !deliveryIsMark(conv);
        return (
          <li
            key={conv.thread_id}
            data-testid="conversation-row"
            data-thread-id={conv.thread_id}
            aria-current={selected ? 'true' : undefined}
            className={`relative flex min-h-11 flex-col gap-0.5 border-b border-l-[3px] border-b-outline px-3 py-2 hover:bg-surface-2/50 ${
              selected ? 'border-l-ink-1 bg-surface-3' : 'border-l-transparent'
            }`}
          >
            <div className="flex items-baseline justify-between gap-2">
              <a
                href={`#conversations/${encodeURIComponent(conv.thread_id)}`}
                data-testid="conversation-link"
                aria-label={t('conversations.viewConversation', { phone: conv.phone_masked })}
                className={`font-semibold text-ink-1 max-md:flex max-md:min-h-11 max-md:items-center after:absolute after:inset-0 after:content-[''] ${linkClass}`}
              >
                {conv.phone_masked}
              </a>
              <span className="shrink-0 text-meta text-ink-2">
                {formatDateTime(conv.last_activity_at, locale)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-2">
              {conv.lead_id != null ? (
                <a
                  href={`#leads/${encodeURIComponent(conv.lead_id)}`}
                  data-testid="conversation-lead-link"
                  aria-label={t('conversations.viewLead')}
                  className={`relative z-10 min-w-0 truncate max-md:min-h-6 text-ink-2 ${linkClass}`}
                >
                  {conv.lead_display_name ??
                    t(conv.funnel_stage ? 'conversations.newLead' : 'conversations.openLead')}
                </a>
              ) : (
                <span data-testid="conversation-lead-none" className="text-ink-3">
                  —
                </span>
              )}
              <StatusMark conv={conv} />
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-meta text-ink-2">
              {stageIsMark ? null : (
                <span>
                  {conv.funnel_stage
                    ? t(`funnelStage.${conv.funnel_stage}`, { defaultValue: conv.funnel_stage })
                    : '—'}
                </span>
              )}
              <span>{conv.channel}</span>
              <span>{conv.waba_number ?? '—'}</span>
              <span>{conv.script_step ?? '—'}</span>
              <span>
                <span data-testid="conversation-turns">{conv.inbound_message_count}</span>{' '}
                {t('conversations.turnsShort')}
              </span>
              {conv.consent_state !== 'granted' ? (
                <ConsentBadge state={conv.consent_state} />
              ) : null}
              {!deliveryIsMark(conv) && conv.delivery_status !== 'ok' ? (
                <DeliveryBadge status={conv.delivery_status} />
              ) : null}
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export const ConversationsPage = ({
  apiClient,
  selectedThreadId = null,
}: ConversationsPageProps) => {
  const { t, i18n } = useTranslation();
  const {
    items,
    nextCursor,
    approxTotal,
    detailCompleteness,
    phase,
    loadingMore,
    errored,
    load,
    loadMore,
  } = useConversationsQuery(apiClient);

  // Seeded from the remembered filter set so a returning operator lands where
  // they left off.
  const [appliedQuery, setAppliedQuery] = useState<ConversationsQuery>(initialQuery);
  const didMount = useRef(false);

  useEffect(() => {
    if (didMount.current) return;
    didMount.current = true;
    floating(load(appliedQuery));
  }, [load, appliedQuery]);

  const handleApply = useCallback(
    (query: ConversationsQuery) => {
      setAppliedQuery(query);
      savePersistedFilters(CONVERSATIONS_FILTERS_KEY, persistableQuery(query));
      floating(load(query));
    },
    [load],
  );

  const handleRetry = useCallback(() => {
    floating(load(appliedQuery));
  }, [appliedQuery, load]);

  // Global "refresh" hotkey (see AppShell) re-runs the current query.
  useEffect(() => {
    const onRefresh = () => floating(load(appliedQuery));
    window.addEventListener('tcn:refresh', onRefresh);
    return () => window.removeEventListener('tcn:refresh', onRefresh);
  }, [load, appliedQuery]);

  if (phase === 'unauthorized') {
    return <UnauthorizedScreen />;
  }

  const busy = phase === 'loading' || loadingMore;
  // 'single_replica' is the honest healthy state; anything else is degraded.
  const degraded = detailCompleteness != null && detailCompleteness !== 'single_replica';

  const renderBranch1 = () => {
    if (phase === 'loading' && items.length === 0)
      return <Skeleton data-testid="conversations-loading" label={t('conversations.loading')} />;
    if (phase === 'error' && items.length === 0)
      return (
        <Notice
          tone="danger"
          data-testid="conversations-error"
          data-focal="true"
          action={
            <Button data-testid="conversations-retry" onClick={handleRetry}>
              {t('conversations.retry')}
            </Button>
          }
        >
          {t('conversations.errorBody')}
        </Notice>
      );
    if (items.length === 0)
      return <EmptyState data-testid="conversations-empty" title={t('conversations.empty')} />;
    return (
      <ConversationsList items={items} locale={i18n.language} selectedThreadId={selectedThreadId} />
    );
  };

  return (
    <section
      aria-labelledby="conversations-heading"
      className="flex min-h-0 flex-col md:h-full md:border-r md:border-outline"
    >
      <div className="flex flex-col gap-3 pb-3 md:pr-3">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <h1 id="conversations-heading" className="text-h1 text-ink-1">
            {t('conversations.title')}
          </h1>
          <p role="status" className="text-meta text-ink-3" data-testid="conversations-summary">
            {t('conversations.showing', { count: items.length })}
            {approxTotal != null
              ? ` · ${t('conversations.approxTotal', { count: approxTotal })}`
              : ''}
          </p>
        </header>

        <ConversationsFilters onApply={handleApply} busy={busy} applied={appliedQuery} />

        {degraded ? (
          <p
            role="status"
            data-testid="conversations-degraded-note"
            className="rounded-lg bg-status-degraded/15 px-4 py-2 text-meta text-status-degraded ring-1 ring-inset ring-status-degraded/40"
          >
            {t('conversations.degradedNote')}
          </p>
        ) : null}

        {/* SINGLE error surface — compact banner when rows survive, focal
          (elev-2 status-down ring) when the list is empty; never both. */}
        {errored && items.length > 0 ? (
          <p
            role="alert"
            data-testid="conversations-error-banner"
            className="rounded-lg bg-status-down/15 px-4 py-2 text-meta text-status-down ring-1 ring-inset ring-status-down/40"
          >
            {t('conversations.errorBody')}
          </p>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 md:overflow-y-auto" data-testid="conversations-scroll">
        {renderBranch1()}

        {items.length > 0 ? (
          <div className="flex items-center gap-3 py-3 pl-3">
            {nextCursor != null ? (
              <Button
                data-testid="conversations-load-more"
                onClick={() => floating(loadMore())}
                disabled={loadingMore}
                aria-busy={loadingMore}
                className="min-h-11"
              >
                {loadingMore ? t('conversations.loadingMore') : t('conversations.loadMore')}
              </Button>
            ) : (
              <p data-testid="conversations-end" className="text-meta text-ink-3">
                {t('conversations.endOfList')}
              </p>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
};
